import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import { Popover } from 'antd';
import OverrideTable from 'components/core/fields/transformation_parameters/OverrideTable';
import TransformationParameter from 'components/core/fields/transformation_parameters/TransformationParameter';
import TransformationTypeField from 'components/core/fields/TransformationTypeField';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import Constants from 'utils/Constants';
import { colorLabelBlue } from 'styles/base';
import ProcessUtils from 'utils/ProcessUtils';
import { popoverErrorStyle } from 'styles/base.js';


export default class TransformationTypeParameter extends TransformationParameter {
    static propTypes = {
        wizardMode: PropTypes.bool
    };

    static defaultProps = {
        parameterNames: ['transformation_type'],
        isOverridable: true,
        isDefaultValueVisibleInitially: true,
        infoBoxText: '',
        flex: '1'
    };

    renderQuestionText = () => {
        return (
            <span>What algorithm should be used to transform variable data dimensioned by
                <span className={css`color:${colorLabelBlue}`}> {this.props.dimensionName}</span>?</span>
        );
    };

    renderDefaultComponent = () => {
        return (
            <Flexbox>
                <TransformationTypeField
                    process={this.props.process}
                    coordinateSystemName={this.props.coordinateSystemName}
                    dimensionName={this.props.dimensionName}
                    displayQuestion={false}
                    radioDisplay
                    hasWritePermissions={this.props.hasWritePermissions}
                />
            </Flexbox>
        );
    };

    renderOverrideTable = () => {
        return (
            <TransformationTypeOverrideTable
                process={this.props.process}
                hasWritePermissions={this.props.hasWritePermissions}
                parameterNames={this.props.parameterNames}
                coordinateSystemName={this.props.coordinateSystemName}
                dimensionName={this.props.dimensionName}
                wizardMode={this.props.wizardMode}
            />
        );
    }
}

class TransformationTypeOverrideTable extends OverrideTable {

    getDisabledMessage = () => {
        return 'Wizard mode sets directly mapped variables as passthrough. To change this setting, edit the process in standard mode.';
    };

    onValueChange = (fieldPath, value, data) => {
        const parameterName = this.props.parameterNames[0];
        const inputDatastream = data.inputDatastream;
        const variableName = data.retrievedVariable;

        const editor = ProcessUtils.getEditor(this.props.process);

        value = editor.getFormattedValue(value);
        editor.updateTransformationParameterOverride(parameterName, this.props.coordinateSystemName, this.props.dimensionName, inputDatastream, variableName, value);
        editor.commit();
    };

    isCheckboxDisabled = (rowName) => {
        let disabled = false;
        const parameterName = this.props.parameterNames[0];
        let value = null;

        this.rowData.forEach(row => {
            if (row.key === rowName) {
                value = row.data.value[parameterName];
            }
        });

        if (this.props.wizardMode && value === Constants.TRANSFORM_TYPE_PASSTHROUGH) {
            disabled = true;
        }

        return disabled;
    };

    renderCheckbox(rowName) {
        const onClick = () => {
            this.toggleSelected(rowName);
        };
        const selected = this.isSelected(rowName);

        const disabled = this.isCheckboxDisabled(rowName);

        const checkboxComponent = (
            <input
                className="table-select"
                type="checkbox"
                checked={selected}
                onClick={onClick}
                onChange={this.onChange}
                style={{ width: '20px' }}
                disabled={disabled}
            />
        );

        if (disabled) {
            return (
                <Popover
                    content={this.getDisabledMessage()}
                    placement={'topLeft'}
                    overlayClassName={popoverErrorStyle}
                >
                    {checkboxComponent}
                </Popover>
            );
        } else {
            return checkboxComponent;
        }
    }

    toggleSelected = (rowName) => {
        let selectedItems = Object.assign({}, this.state.selectedItems);
        if (!rowName) {
            // select all toggle pressed
            if (Object.keys(selectedItems).length === this.getRowNames().length) {
                selectedItems = {}; // de-select all
            } else {
                this.getRowNames().forEach(name => {
                    if (!this.isCheckboxDisabled(name)) {
                        selectedItems[name] = true; // select all
                    }
                });
            }

        } else {
            const selected = selectedItems[rowName] ? false : true;
            if (selected) {
                selectedItems[rowName] = true;
            } else {
                delete selectedItems[rowName];
            }

        }
        this.setState({ selectedItems });
    }

    renderTransformationType = (data) => {
        const value = this.getValue(data);

        let isDisabled = !this.props.hasWritePermissions;
        if (!isDisabled && this.props.wizardMode && value === Constants.TRANSFORM_TYPE_PASSTHROUGH) {
            isDisabled = true;
        }

        return (
            <RIEComboBox
                value={Constants.getOptionById(value, Constants.VARIABLE_OVERRIDE_TRANSFORM_TYPES)}
                options={Constants.VARIABLE_OVERRIDE_TRANSFORM_TYPES}
                change={this.onValueChange}
                fieldPath={this.getFieldPath(this.props.parameterNames[0], data)}
                onChangeData={data}
                isDisabled={isDisabled}
                disabledMessage={this.getDisabledMessage()}
                processId={this.props.process.name}
            />
        );
    };

    getAdditionalColumns() {
        return ([
            {
                property: 'data',
                header: {
                    label: 'Transformation Method',
                    props: {
                        style: {
                            whiteSpace: 'nowrap',
                        }
                    }
                },
                cell: {
                    formatters: [
                        data => { return this.renderTransformationType(data); }
                    ]
                }
            }
        ]);
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/TransformationTypeParameter.js